import type { ExerciseDefinition, MovementPattern, MuscleGroup } from '../types';
import { MUSCLE_GROUP_LABELS, MOVEMENT_PATTERN_LABELS, EXERCISE_TAG_LABELS } from '../types';

/**
 * Master exercise database - every exercise the builder offers.
 * Each entry carries movement pattern, muscle targeting, tag, and sensible defaults.
 */
export const EXERCISE_DB: ExerciseDefinition[] = [
  // ═══════════════════════════════════════════
  // SQUAT PATTERN
  // ═══════════════════════════════════════════
  {
    id: 'back-squat',
    name: 'Przysiad ze sztangą z tyłu',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'front-squat',
    name: 'Przysiad ze sztangą z przodu',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'abs', 'upper_back'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'goblet-squat',
    name: 'Przysiad pucharowy',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'leg-press',
    name: 'Prasa nóg',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-12', defaultRPE: 8, defaultRest: 150, defaultTempo: '3-0-2-0',
  },
  {
    id: 'bulgarian-split-squat',
    name: 'Bułgarski przysiad',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 8, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'hack-squat',
    name: 'Przysiad Hacka',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-0-2-0',
  },

  // ═══════════════════════════════════════════
  // HINGE PATTERN
  // ═══════════════════════════════════════════
  {
    id: 'conventional-deadlift',
    name: 'Martwy ciąg klasyczny',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back', 'lats', 'traps', 'forearms', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 240, defaultTempo: '2-0-1-1',
  },
  {
    id: 'romanian-deadlift',
    name: 'Martwy ciąg rumuński',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'sumo-deadlift',
    name: 'Martwy ciąg sumo',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'quads', 'upper_back', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 240, defaultTempo: '2-0-1-1',
  },
  {
    id: 'good-morning',
    name: 'Dzień dobry',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'hip-thrust',
    name: 'Wypychanie bioder',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-12', defaultRPE: 8, defaultRest: 120, defaultTempo: '2-1-1-0',
  },
  {
    id: 'cable-pull-through',
    name: 'Przeciąganie linki',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // HORIZONTAL PUSH
  // ═══════════════════════════════════════════
  {
    id: 'barbell-bench-press',
    name: 'Wyciskanie sztangi leżąc',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'dumbbell-bench-press',
    name: 'Wyciskanie hantli leżąc',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'incline-barbell-press',
    name: 'Wyciskanie sztangi skośnie',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'incline-db-press',
    name: 'Wyciskanie hantli skośnie',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'dips',
    name: 'Pompki na poręczach',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-0-2-0',
  },

  // ═══════════════════════════════════════════
  // VERTICAL PUSH
  // ═══════════════════════════════════════════
  {
    id: 'overhead-press',
    name: 'Wyciskanie nad głowę',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '2-1-2-0',
  },
  {
    id: 'db-shoulder-press',
    name: 'Wyciskanie hantli na ramiona',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'arnold-press',
    name: 'Wyciskanie Arnolda',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'landmine-press',
    name: 'Wyciskanie landmine',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['chest', 'triceps', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-0-2-0',
  },

  // ═══════════════════════════════════════════
  // HORIZONTAL PULL
  // ═══════════════════════════════════════════
  {
    id: 'barbell-row',
    name: 'Wiosłowanie sztangą w opadzie',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'dumbbell-row',
    name: 'Wiosłowanie hantlą',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'cable-row',
    name: 'Wiosłowanie siedząc',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 't-bar-row',
    name: 'Wiosłowanie T-bar',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-10', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'chest-supported-row',
    name: 'Wiosłowanie z podparciem',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // VERTICAL PULL
  // ═══════════════════════════════════════════
  {
    id: 'pull-up',
    name: 'Podciąganie nachwytem',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'forearms', 'rear_delts'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-10', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'chin-up',
    name: 'Podciąganie podchwytem',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-10', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'lat-pulldown',
    name: 'Ściąganie wyciągu',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // CARRY
  // ═══════════════════════════════════════════
  {
    id: 'farmers-walk',
    name: "Farmer's Walk",
    movementPattern: 'carry',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['traps', 'abs', 'glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '40m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'suitcase-carry',
    name: 'Przenoszenie walizki',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['forearms', 'traps', 'glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30m/side', defaultRPE: 7, defaultRest: 90, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // CORE
  // ═══════════════════════════════════════════
  {
    id: 'plank',
    name: 'Deska',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '30-60s', defaultRPE: 7, defaultRest: 60, defaultTempo: '-',
  },
  {
    id: 'ab-wheel',
    name: 'Rollout z kółkiem',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'lats'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 8, defaultRest: 90, defaultTempo: '3-0-2-0',
  },
  {
    id: 'cable-woodchop',
    name: 'Rąbanie drewna',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15/side', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-0-2-0',
  },
  {
    id: 'hanging-leg-raise',
    name: 'Unoszenie nóg w zwisie',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'pallof-press',
    name: 'Pallof Press',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/side', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-2-2-0',
  },

  // ═══════════════════════════════════════════
  // ISOLATION - UPPER
  // ═══════════════════════════════════════════
  {
    id: 'lateral-raise',
    name: 'Unoszenie boczne',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['traps'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'rear-delt-fly',
    name: 'Rozpietki na tył barków',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'face-pull',
    name: 'Wyciąg do twarzy',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'barbell-curl',
    name: 'Uginanie ze sztangą',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'hammer-curl',
    name: 'Młotek',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'tricep-pushdown',
    name: 'Prostowanie tricepsów',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'overhead-tricep-ext',
    name: 'Wyprост triceps nad głowę',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'cable-fly',
    name: 'Rozpiętki na wyciągu',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'pec-deck',
    name: 'Peck-deck / Rozpiętki',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // ISOLATION - LOWER
  // ═══════════════════════════════════════════
  {
    id: 'leg-extension',
    name: 'Prostowanie nóg',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'leg-curl',
    name: 'Uginanie nóg leżąc',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['calves'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'standing-calf-raise',
    name: 'Wspięcia na palce stojąc',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 4, defaultReps: '10-15', defaultRPE: 8, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'seated-calf-raise',
    name: 'Wspięcia na palce siedząc',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'hip-adduction',
    name: 'Przywodzenie bioder',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['glutes'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // SQUAT - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'zercher-squat',
    name: 'Przysiad Zerchera',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'abs', 'biceps', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'overhead-squat',
    name: 'Przysiad ze sztangą nad głową',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'abs', 'front_delts', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '5-8', defaultRPE: 7.5, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'safety-bar-squat',
    name: 'Przysiad z safety barem',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs', 'upper_back'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'box-squat',
    name: 'Przysiad na skrzynkę',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },
  {
    id: 'belt-squat',
    name: 'Przysiad z pasem (belt squat)',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'pendulum-squat',
    name: 'Przysiad wahadłowy',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'sissy-squat',
    name: 'Sissy squat',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['hip_flexors'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'smith-squat',
    name: 'Przysiad w suwnicy Smitha',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-0-2-0',
  },
  {
    id: 'walking-lunge',
    name: 'Wykroki w marszu',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-0-2-0',
  },
  {
    id: 'reverse-lunge',
    name: 'Wykrok w tył',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'barbell-lunge',
    name: 'Wykrok ze sztangą',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/noga', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'step-up',
    name: 'Wchodzenie na podwyższenie',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-0-2-0',
  },
  {
    id: 'lateral-lunge',
    name: 'Wykrok boczny',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'pistol-squat',
    name: 'Przysiad na jednej nodze',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '5-8/noga', defaultRPE: 8.5, defaultRest: 120, defaultTempo: '3-1-2-0',
  },

  // ═══════════════════════════════════════════
  // HINGE - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'trap-bar-deadlift',
    name: 'Martwy ciąg z trap barem',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'quads', 'upper_back', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8.5, defaultRest: 210, defaultTempo: '2-0-1-1',
  },
  {
    id: 'deficit-deadlift',
    name: 'Martwy ciąg z deficytu',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'quads', 'upper_back', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 240, defaultTempo: '2-0-1-1',
  },
  {
    id: 'block-pull',
    name: 'Martwy ciąg z klocków',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '3-5', defaultRPE: 8.5, defaultRest: 240, defaultTempo: '2-0-1-1',
  },
  {
    id: 'stiff-leg-deadlift',
    name: 'Martwy ciąg na prostych nogach',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'single-leg-rdl',
    name: 'RDL na jednej nodze',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/noga', defaultRPE: 7, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'kettlebell-swing',
    name: 'Wymachy kettlebella',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'abs', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '15-20', defaultRPE: 7, defaultRest: 90, defaultTempo: '-',
  },
  {
    id: 'glute-ham-raise',
    name: 'Unoszenie glute-ham (GHR)',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'calves'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 8, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'nordic-curl',
    name: 'Uginanie nordyckie',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['calves', 'glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '4-8', defaultRPE: 9, defaultRest: 150, defaultTempo: '4-0-1-0',
  },
  {
    id: 'hyperextension',
    name: 'Hiperekstensjie (ławka rzymska)',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'abs'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'reverse-hyper',
    name: 'Odwrócona hiperekstensjia',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'cable-kickback',
    name: 'Odwodzenie nogi na wyciągu',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/noga', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'barbell-hip-thrust',
    name: 'Hip thrust ze sztangą',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-12', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-2-1-0',
  },
  {
    id: 'single-leg-hip-thrust',
    name: 'Hip thrust na jednej nodze',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-1-0',
  },
  {
    id: 'glute-bridge',
    name: 'Mostek biodrowy',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-2-1-0',
  },

  // ═══════════════════════════════════════════
  // HORIZONTAL PUSH - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'close-grip-bench',
    name: 'Wyciskanie wąskim chwytem',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest', 'front_delts'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'decline-bench-press',
    name: 'Wyciskanie na ławce ujemnej',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'decline-db-press',
    name: 'Wyciskanie hantli na ujemnej',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'push-up',
    name: 'Pompki',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-20', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'weighted-push-up',
    name: 'Pompki z obciążeniem',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 8, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'floor-press',
    name: 'Wyciskanie z podłogi',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '3-1-2-0',
  },
  {
    id: 'machine-chest-press',
    name: 'Wyciskanie na maszynie (klatka)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'spoto-press',
    name: 'Spoto press',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '5-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-2-2-0',
  },
  {
    id: 'larsen-press',
    name: 'Larsen press (bez nóg)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '3-1-2-0',
  },

  // ═══════════════════════════════════════════
  // VERTICAL PUSH - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'push-press',
    name: 'Push press',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps', 'quads', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8, defaultRest: 180, defaultTempo: '-',
  },
  {
    id: 'z-press',
    name: 'Z-press (siedząc na podłodze)',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'machine-shoulder-press',
    name: 'Wyciskanie na maszynie (ramiona)',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'behind-neck-press',
    name: 'Wyciskanie zza głowy',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'viking-press',
    name: 'Viking press',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['triceps', 'abs', 'chest'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-0-2-0',
  },
  {
    id: 'handstand-push-up',
    name: 'Pompki w staniu na rękach',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['triceps', 'side_delts', 'abs', 'traps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '5-10', defaultRPE: 8.5, defaultRest: 150, defaultTempo: '2-0-2-0',
  },
  {
    id: 'smith-shoulder-press',
    name: 'Wyciskanie na Smithie (ramiona)',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // HORIZONTAL PULL - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'pendlay-row',
    name: 'Wiosłowanie Pendlaya',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 8, defaultRest: 150, defaultTempo: '1-0-1-1',
  },
  {
    id: 'meadows-row',
    name: 'Wiosłowanie Meadowsa',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/ręka', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'seal-row',
    name: 'Wiosłowanie leżąc (seal row)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-3-0',
  },
  {
    id: 'kroc-row',
    name: 'Wiosłowanie Kroca (ciężkie)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'rear_delts', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-20/ręka', defaultRPE: 9, defaultRest: 120, defaultTempo: '1-0-1-0',
  },
  {
    id: 'machine-row',
    name: 'Wiosłowanie na maszynie',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'inverted-row',
    name: 'Wiosłowanie na drążku (australijskie)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delts', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'helms-row',
    name: 'Wiosłowanie Helmsa',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'landmine-row',
    name: 'Wiosłowanie landmine',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delts', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // VERTICAL PULL - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'neutral-grip-pullup',
    name: 'Podciąganie chwytem neutralnym',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '6-10', defaultRPE: 8, defaultRest: 150, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wide-grip-pulldown',
    name: 'Ściąganie wyciągu szerokim chwytem',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'close-grip-pulldown',
    name: 'Ściąganie wyciągu wąskim chwytem',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'straight-arm-pulldown',
    name: 'Ściąganie prostymi ramionami',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['abs', 'rear_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'cable-pullover',
    name: 'Pullover na wyciągu',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['chest', 'abs'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'dumbbell-pullover',
    name: 'Pullover z hantlą',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['chest', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'weighted-pull-up',
    name: 'Podciąganie z obciążeniem',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 180, defaultTempo: '2-1-2-0',
  },
  {
    id: 'machine-pulldown',
    name: 'Ściąganie na maszynie',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // CARRY - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'overhead-carry',
    name: 'Przenoszenie nad głową',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['front_delts', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30m', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '-',
  },
  {
    id: 'trap-bar-carry',
    name: 'Przenoszenie z trap barem',
    movementPattern: 'carry',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['traps', 'abs', 'glutes', 'quads'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '40m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'rack-carry',
    name: 'Przenoszenie w pozycji rack',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['front_delts', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30m', defaultRPE: 7, defaultRest: 90, defaultTempo: '-',
  },
  {
    id: 'waiter-carry',
    name: 'Przenoszenie kelnera',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['front_delts', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30m/ręka', defaultRPE: 7, defaultRest: 90, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // CORE - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'russian-twist',
    name: 'Skręty rosyjskie',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20/stronę', defaultRPE: 7, defaultRest: 60, defaultTempo: '1-0-1-0',
  },
  {
    id: 'dead-bug',
    name: 'Dead bug',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/stronę', defaultRPE: 6, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'bird-dog',
    name: 'Bird dog',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['glutes', 'upper_back'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/stronę', defaultRPE: 6, defaultRest: 60, defaultTempo: '3-2-2-0',
  },
  {
    id: 'side-plank',
    name: 'Deska boczna',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'glutes'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '30-45s/stronę', defaultRPE: 7, defaultRest: 60, defaultTempo: '-',
  },
  {
    id: 'reverse-crunch',
    name: 'Odwrócone spięcia brzucha',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'cable-crunch',
    name: 'Spięcia brzucha na wyciągu',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: [],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'decline-sit-up',
    name: 'Spięcia na ławce ujemnej',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'dragon-flag',
    name: 'Dragon flag',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'lats'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '6-10', defaultRPE: 9, defaultRest: 120, defaultTempo: '4-1-2-0',
  },
  {
    id: 'landmine-rotation',
    name: 'Rotacja z landmine',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'front_delts'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/stronę', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-0-2-0',
  },
  {
    id: 'l-sit-hold',
    name: 'L-sit (zwis/podpór)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'quads'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-30s', defaultRPE: 8, defaultRest: 90, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // ISOLATION UPPER - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'concentration-curl',
    name: 'Uginanie skupione (concentration)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12/ręka', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'preacher-curl',
    name: 'Uginanie na modlitewniku',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'incline-db-curl',
    name: 'Uginanie hantli na skośnej',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'spider-curl',
    name: 'Spider curl',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'cable-curl',
    name: 'Uginanie na wyciągu',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'ez-bar-curl',
    name: 'Uginanie ze sztangą łamaną',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'reverse-curl',
    name: 'Uginanie nawrotne',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['biceps'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'skull-crusher',
    name: 'Francuskie wyciskanie (skull crusher)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'tricep-dip-machine',
    name: 'Prostowanie tricepsów na maszynie',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'tricep-kickback',
    name: 'Odwodzenie hantli (kickback)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'cable-lateral-raise',
    name: 'Unoszenie boczne na wyciągu',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['traps'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'machine-lateral-raise',
    name: 'Unoszenie boczne na maszynie',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'front-raise',
    name: 'Unoszenie przód',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'upright-row',
    name: 'Wiosłowanie (upright row)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['traps', 'front_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'barbell-shrug',
    name: 'Szragi ze sztangą',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'traps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 4, defaultReps: '10-15', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-2-1-0',
  },
  {
    id: 'dumbbell-shrug',
    name: 'Szragi z hantlami',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'traps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'wrist-curl',
    name: 'Uginanie nadgarstków',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'forearms',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'reverse-wrist-curl',
    name: 'Odwrócone uginanie nadgarstków',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'forearms',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'incline-db-fly',
    name: 'Rozpiętki hantlami na skośnej',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'dumbbell-fly',
    name: 'Rozpiętki hantlami na płaskiej',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'machine-rear-delt',
    name: 'Tył barków na maszynie',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'bayesian-curl',
    name: 'Bayesian curl na wyciągu',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12/ręka', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // ISOLATION LOWER - DODATKOWE
  // ═══════════════════════════════════════════
  {
    id: 'seated-leg-curl',
    name: 'Uginanie nóg siedząc',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['calves'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'donkey-calf-raise',
    name: 'Wspięcia na palce oślę',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'hip-abduction',
    name: 'Odwodzenie bioder',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hip_flexors'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'single-leg-press',
    name: 'Prasa nóg jednoosobowo',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '3-0-2-0',
  },
  {
    id: 'single-leg-extension',
    name: 'Prostowanie jednej nogi',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/noga', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'tibialis-raise',
    name: 'Unoszenie stóp (tibialis)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'leg-press-calf-raise',
    name: 'Wspięcia na prasie nóg',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 4, defaultReps: '12-15', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  // ═══════════════════════════════════════════
  // EXPANDED - SQUAT PATTERN
  // ═══════════════════════════════════════════
  {
    id: 'landmine-squat',
    name: 'Przysiad z landmine',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'pause-squat',
    name: 'Przysiad z pauzą',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 180, defaultTempo: '3-2-2-0',
  },
  {
    id: 'tempo-squat',
    name: 'Przysiad tempo',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '4-1-2-0',
  },
  {
    id: 'heels-elevated-squat',
    name: 'Przysiad z podwyższonymi piętami',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'anderson-squat',
    name: 'Przysiad Andersona (z pinów)',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '3-5', defaultRPE: 9, defaultRest: 240, defaultTempo: '1-1-1-0',
  },
  {
    id: 'bodyweight-squat',
    name: 'Przysiad z masą ciała',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6, defaultRest: 60, defaultTempo: '2-0-2-0',
  },
  {
    id: 'jump-squat',
    name: 'Przysiad z wyskoku',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'calves'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-0-1-0',
  },
  {
    id: 'wall-sit',
    name: 'Krzesełko (izometria)',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '30-60s', defaultRPE: 7, defaultRest: 90, defaultTempo: '0-0-0-0',
  },
  {
    id: 'split-squat',
    name: 'Przysiad wykroczny',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'db-lunge',
    name: 'Wykroki z hantlami',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-0-2-0',
  },
  {
    id: 'curtsy-lunge',
    name: 'Wykrok krzyżowy',
    movementPattern: 'squat',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['quads', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/noga', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - HINGE PATTERN
  // ═══════════════════════════════════════════
  {
    id: 'snatch-grip-deadlift',
    name: 'Martwy ciąg chwyt szarpnięciowy',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back', 'traps', 'lats'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '4-6', defaultRPE: 8.5, defaultRest: 240, defaultTempo: '2-0-1-1',
  },
  {
    id: 'pause-deadlift',
    name: 'Martwy ciąg z pauzą',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '3-5', defaultRPE: 8, defaultRest: 210, defaultTempo: '2-2-1-0',
  },
  {
    id: 'db-romanian-deadlift',
    name: 'Martwy ciąg rumuński z hantlami',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'kb-deadlift',
    name: 'Martwy ciąg z kettlebell',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'rack-pull',
    name: 'Ciąg z klocków / rack pull',
    movementPattern: 'hinge',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['glutes', 'hamstrings', 'traps', 'forearms'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '3-5', defaultRPE: 8.5, defaultRest: 210, defaultTempo: '1-0-1-1',
  },
  {
    id: 'glute-bridge-barbell',
    name: 'Most pośladkowy ze sztangą',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-1-0',
  },
  {
    id: 'banded-hip-thrust',
    name: 'Wypychanie bioder z gumą',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-1-0',
  },
  {
    id: 'kb-swing',
    name: 'Kettlebell swing jednoręcznie',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'abs', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '12-15/ręka', defaultRPE: 7, defaultRest: 90, defaultTempo: '1-0-1-0',
  },
  {
    id: 'cable-romanian-deadlift',
    name: 'Martwy ciąg rumuński na wyciągu',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '3-1-2-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - HORIZONTAL PUSH
  // ═══════════════════════════════════════════
  {
    id: 'db-floor-press',
    name: 'Wyciskanie hantli z podłogi',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'smith-bench-press',
    name: 'Wyciskanie na ławce Smith',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 150, defaultTempo: '3-0-2-0',
  },
  {
    id: 'machine-incline-press',
    name: 'Wyciskanie na maszynie skośnej',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'resistance-band-push-up',
    name: 'Pompki z gumą oporową',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'svend-press',
    name: 'Svend press (ściskanie talerzy)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'hex-press',
    name: 'Hex press (ściskanie hantli)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'deficit-push-up',
    name: 'Pompki z deficytem',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'diamond-push-up',
    name: 'Pompki diamentowe',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - VERTICAL PUSH
  // ═══════════════════════════════════════════
  {
    id: 'seated-db-press',
    name: 'Wyciskanie hantli siedząc',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'pike-push-up',
    name: 'Pompki szczupak',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['triceps', 'side_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'cable-shoulder-press',
    name: 'Wyciskanie nad głowę na wyciągu',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'plate-front-raise',
    name: 'Unoszenie talerza przed siebie',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['abs'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'bottoms-up-kb-press',
    name: 'Wyciskanie kettlebell dnem do góry',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8/ręka', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - HORIZONTAL PULL
  // ═══════════════════════════════════════════
  {
    id: 'gorilla-row',
    name: 'Gorilla row (kettlebell)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/ręka', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'single-arm-cable-row',
    name: 'Wiosłowanie na wyciągu jednoręcznie',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/ręka', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'batwing-row',
    name: 'Batwing row (izometria)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-3-2-0',
  },
  {
    id: 'band-pull-apart',
    name: 'Rozciąganie gumy (pull apart)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6, defaultRest: 45, defaultTempo: '2-1-2-0',
  },
  {
    id: 'prone-y-raise',
    name: 'Y-raise na ławce',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 6.5, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'wide-grip-cable-row',
    name: 'Wiosłowanie na wyciągu szerokim chwytem',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7.5, defaultRest: 120, defaultTempo: '2-1-3-0',
  },
  {
    id: 'bodyweight-row',
    name: 'Wiosłowanie ciałem (TRX/drążek)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - VERTICAL PULL
  // ═══════════════════════════════════════════
  {
    id: 'single-arm-pulldown',
    name: 'Ściąganie na wyciągu jednoręcznie',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12/ręka', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-3-0',
  },
  {
    id: 'behind-neck-pulldown',
    name: 'Ściąganie za głowę',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'biceps', 'rear_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-3-0',
  },
  {
    id: 'v-grip-pulldown',
    name: 'Ściąganie V-grip',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-3-0',
  },
  {
    id: 'commando-pull-up',
    name: 'Podciągnięcie komandosa',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'forearms', 'abs'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'band-assisted-pull-up',
    name: 'Podciągnięcie z gumą (asystowane)',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-1-2-0',
  },
  {
    id: 'negative-pull-up',
    name: 'Podciągnięcie negatywne',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['biceps', 'upper_back', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '5-6', defaultRPE: 8, defaultRest: 120, defaultTempo: '5-0-0-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - CARRY
  // ═══════════════════════════════════════════
  {
    id: 'zercher-carry',
    name: 'Przenoszenie Zerchera',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['biceps', 'upper_back', 'quads'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30-40m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'sandbag-carry',
    name: 'Przenoszenie worka z piaskiem',
    movementPattern: 'carry',
    primaryMuscle: 'abs',
    secondaryMuscles: ['upper_back', 'glutes', 'quads', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '30-40m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'bottoms-up-carry',
    name: 'Przenoszenie kettlebell dnem do góry',
    movementPattern: 'carry',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['front_delts', 'abs'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '20-30m/ręka', defaultRPE: 7, defaultRest: 60, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - CORE
  // ═══════════════════════════════════════════
  {
    id: 'stir-the-pot',
    name: 'Mieszanie garnka (piłka)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-0-2-0',
  },
  {
    id: 'suitcase-deadlift',
    name: 'Martwy ciąg walizkowy',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['forearms', 'glutes', 'hamstrings'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/strona', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-0-2-0',
  },
  {
    id: 'copenhagen-plank',
    name: 'Plank kopenhaski',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '20-30s/strona', defaultRPE: 7, defaultRest: 60, defaultTempo: '0-0-0-0',
  },
  {
    id: 'hollow-body-hold',
    name: 'Hollow body hold',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '20-40s', defaultRPE: 7, defaultRest: 60, defaultTempo: '0-0-0-0',
  },
  {
    id: 'farmers-walk-trap-bar',
    name: 'Spacer rolnika (trap bar)',
    movementPattern: 'carry',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['traps', 'abs', 'glutes'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '40-60m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'half-kneeling-pallof',
    name: 'Pallof press w półklęku',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'glutes'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/strona', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'medicine-ball-slam',
    name: 'Rzut piłką lekarską o podłogę',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['lats', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '1-0-1-0',
  },
  {
    id: 'v-up',
    name: 'V-up (scyzoryki)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '1-0-1-0',
  },
  {
    id: 'mountain-climbers',
    name: 'Mountain climbers',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'front_delts'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '20-30', defaultRPE: 7, defaultRest: 45, defaultTempo: '0-0-0-0',
  },
  {
    id: 'bicycle-crunch',
    name: 'Crunche rowerowe',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 45, defaultTempo: '1-0-1-0',
  },
  {
    id: 'toe-touch-crunch',
    name: 'Crunch dotyk palców stóp',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6.5, defaultRest: 45, defaultTempo: '1-1-1-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - ISOLATION UPPER
  // ═══════════════════════════════════════════
  {
    id: 'cable-face-pull-rope',
    name: 'Face pull z liną',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6.5, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'zottman-curl',
    name: 'Uginanie Zottmana',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'cross-body-curl',
    name: 'Uginanie cross-body',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: ['forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'overhead-cable-curl',
    name: 'Uginanie na wyciągu górnym',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'drag-curl',
    name: 'Drag curl (ciągnięcie)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-0-3-0',
  },
  {
    id: 'single-arm-tricep-pushdown',
    name: 'Prostowanie na wyciągu jednoręcznie',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/ręka', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'diamond-close-grip-push-up',
    name: 'Pompki wąski chwyt',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },
  {
    id: 'jm-press',
    name: 'JM Press',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '3-1-2-0',
  },
  {
    id: 'lu-raise',
    name: 'Lu raise (boczne + frontalne)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'bus-driver',
    name: 'Bus driver (obroty talerza)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-0-2-0',
  },
  {
    id: 'wrist-roller',
    name: 'Wrist roller (nawijanie sznurka)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'forearms',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '3-5 nawij.', defaultRPE: 7, defaultRest: 60, defaultTempo: '-',
  },
  {
    id: 'dead-hang',
    name: 'Zwis na drążku',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'forearms',
    secondaryMuscles: ['lats'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '30-60s', defaultRPE: 7, defaultRest: 60, defaultTempo: '0-0-0-0',
  },
  {
    id: 'db-lateral-raise-leaning',
    name: 'Unoszenia boczne w nachyleniu',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // EXPANDED - ISOLATION LOWER
  // ═══════════════════════════════════════════
  {
    id: 'banded-leg-curl',
    name: 'Uginanie nóg z gumą',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'leg-curl-db',
    name: 'Uginanie nóg z hantlem (leżąc)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'barbell-calf-raise',
    name: 'Wspięcia na palce ze sztangą',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 4, defaultReps: '12-15', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'cable-hip-abduction',
    name: 'Odwodzenie biodra na wyciągu',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/noga', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'cable-hip-adduction',
    name: 'Przywodzenie biodra na wyciągu',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/noga', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'clamshell',
    name: 'Clamshell (muszelka)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20/strona', defaultRPE: 6.5, defaultRest: 45, defaultTempo: '2-1-2-0',
  },
  {
    id: 'banded-monster-walk',
    name: 'Monster walk z gumą',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hip_flexors'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20/strona', defaultRPE: 6.5, defaultRest: 45, defaultTempo: '-',
  },
  {
    id: 'single-leg-calf-raise',
    name: 'Wspięcia na palce jednonóż',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15/noga', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '2-2-1-0',
  },
  {
    id: 'bodyweight-glute-bridge',
    name: 'Most pośladkowy (masa ciała)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6, defaultRest: 45, defaultTempo: '2-1-1-0',
  },
  {
    id: 'frog-pump',
    name: 'Frog pump (żabie pompki pośladkowe)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: [],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '20-30', defaultRPE: 6.5, defaultRest: 45, defaultTempo: '1-1-1-0',
  },
  {
    id: 'poliquin-step-up',
    name: 'Step-up Poliquina (petersen)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-15/noga', defaultRPE: 7, defaultRest: 60, defaultTempo: '3-1-2-0',
  },
  {
    id: 'reverse-nordic',
    name: 'Reverse nordic (odchylanie)',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: ['hip_flexors'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '6-10', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '3-0-2-0',
  },
  {
    id: 'banded-good-morning',
    name: 'Dzień dobry z gumą',
    movementPattern: 'hinge',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20', defaultRPE: 6.5, defaultRest: 60, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // WARMUP / ROZGRZEWKA
  // ═══════════════════════════════════════════
  {
    id: 'wu-jumping-jacks',
    name: 'Pajacyki (Jumping Jacks)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['quads', 'calves', 'side_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '20-30', defaultRPE: 4, defaultRest: 30, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-high-knees',
    name: 'Wysokie kolana (High Knees)',
    movementPattern: 'core',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['quads', 'calves', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '20/noga', defaultRPE: 4, defaultRest: 30, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-butt-kicks',
    name: 'Kopnięcia pięt do pośladków',
    movementPattern: 'core',
    primaryMuscle: 'hamstrings',
    secondaryMuscles: ['calves', 'quads'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '20/noga', defaultRPE: 3, defaultRest: 30, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-arm-circles',
    name: 'Kółka ramionami',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['front_delts', 'rear_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '15-20', defaultRPE: 3, defaultRest: 15, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-leg-swings-front',
    name: 'Wymach nóg przed-tył',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['hamstrings', 'glutes'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15/noga', defaultRPE: 3, defaultRest: 15, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-leg-swings-lateral',
    name: 'Wymach nóg na boki',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15/noga', defaultRPE: 3, defaultRest: 15, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-hip-circles',
    name: 'Kółka biodrami',
    movementPattern: 'core',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['glutes', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-15/stronę', defaultRPE: 3, defaultRest: 15, defaultTempo: '2-0-2-0',
  },
  {
    id: 'wu-cat-cow',
    name: 'Koci grzbiet / Krowi grzbiet',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['upper_back', 'hip_flexors'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-12', defaultRPE: 3, defaultRest: 15, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-bird-dog',
    name: 'Bird dog (pies myśliwski)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['glutes', 'upper_back'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '8-10/stronę', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-2-2-0',
  },
  {
    id: 'wu-dead-bug',
    name: 'Dead bug (martwy robak)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '8-10/stronę', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-inchworm',
    name: 'Inchworm (gąsienica)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hamstrings', 'chest', 'front_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '6-8', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-0-2-0',
  },
  {
    id: 'wu-worlds-greatest-stretch',
    name: "World's Greatest Stretch",
    movementPattern: 'core',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['hamstrings', 'glutes', 'upper_back', 'chest'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '5-6/stronę', defaultRPE: 3, defaultRest: 30, defaultTempo: '3-2-3-0',
  },
  {
    id: 'wu-bodyweight-squat',
    name: 'Przysiad z ciężarem ciała',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-band-pull-apart',
    name: 'Rozciąganie gumy przed klatką',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '15-20', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-band-dislocate',
    name: 'Dislocate z gumą/kijem',
    movementPattern: 'vertical_push',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['front_delts', 'rear_delts', 'upper_back'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-15', defaultRPE: 3, defaultRest: 30, defaultTempo: '3-0-3-0',
  },
  {
    id: 'wu-foam-roll-upper-back',
    name: 'Foam roller - góra pleców',
    movementPattern: 'core',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['traps'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '60s', defaultRPE: 3, defaultRest: 15, defaultTempo: '-',
  },
  {
    id: 'wu-foam-roll-quads',
    name: 'Foam roller - czworogłowe',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: ['hip_flexors'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '60s', defaultRPE: 3, defaultRest: 15, defaultTempo: '-',
  },
  {
    id: 'wu-foam-roll-glutes',
    name: 'Foam roller - pośladki',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hip_flexors'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '60s', defaultRPE: 3, defaultRest: 15, defaultTempo: '-',
  },
  {
    id: 'wu-foam-roll-it-band',
    name: 'Foam roller - pasmo biodrowo-piszczelowe',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '60s', defaultRPE: 3, defaultRest: 15, defaultTempo: '-',
  },
  {
    id: 'wu-glute-bridge',
    name: 'Mostek biodrowy (rozgrzewka)',
    movementPattern: 'hinge',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hamstrings', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-2-2-0',
  },
  {
    id: 'wu-band-clamshell',
    name: 'Clamshell z gumą',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['hip_flexors'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15/stronę', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-band-monster-walk',
    name: 'Monster walk z gumą',
    movementPattern: 'squat',
    primaryMuscle: 'glutes',
    secondaryMuscles: ['quads', 'hip_flexors'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-12/stronę', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-0-2-0',
  },
  {
    id: 'wu-ytwl',
    name: 'YTWL (aktywacja barków)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps', 'side_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '8-10 każdy', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-2-2-0',
  },
  {
    id: 'wu-band-face-pull',
    name: 'Face pull z gumą (rozgrzewka)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['upper_back', 'traps'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '15-20', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-thoracic-rotation',
    name: 'Rotacja odcinka piersiowego',
    movementPattern: 'core',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '8-10/stronę', defaultRPE: 3, defaultRest: 15, defaultTempo: '3-2-3-0',
  },
  {
    id: 'wu-scapular-push-up',
    name: 'Pompki łopatkowe',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['chest', 'front_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-15', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-banded-external-rotation',
    name: 'Rotacja zewnętrzna z gumą',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'rear_delts',
    secondaryMuscles: ['side_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '12-15/rękę', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-1-2-0',
  },
  {
    id: 'wu-ankle-circles',
    name: 'Kółka kostkami',
    movementPattern: 'isolation_lower',
    primaryMuscle: 'calves',
    secondaryMuscles: [],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '10-15/stronę', defaultRPE: 2, defaultRest: 15, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-mountain-climber',
    name: 'Mountain climbers (alpinista)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'quads', 'front_delts'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '15-20/noga', defaultRPE: 5, defaultRest: 30, defaultTempo: '1-0-1-0',
  },
  {
    id: 'wu-walking-lunge',
    name: 'Wykroki w marszu (rozgrzewka)',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings', 'hip_flexors'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '8-10/noga', defaultRPE: 4, defaultRest: 30, defaultTempo: '2-0-2-0',
  },
  {
    id: 'wu-spiderman-stretch',
    name: 'Spiderman stretch',
    movementPattern: 'core',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['hamstrings', 'glutes', 'abs'],
    tag: 'warmup',
    defaultSets: 2, defaultReps: '6-8/stronę', defaultRPE: 3, defaultRest: 15, defaultTempo: '3-2-3-0',
  },
  {
    id: 'wu-light-rowing',
    name: 'Lekkie wiosłowanie na ergometrze',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'quads', 'abs'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '3-5 min', defaultRPE: 4, defaultRest: 30, defaultTempo: '-',
  },
  {
    id: 'wu-jump-rope',
    name: 'Skakanka',
    movementPattern: 'core',
    primaryMuscle: 'calves',
    secondaryMuscles: ['quads', 'abs'],
    tag: 'warmup',
    defaultSets: 1, defaultReps: '2-3 min', defaultRPE: 4, defaultRest: 30, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - BODYWEIGHT / CALISTHENICS
  // ═══════════════════════════════════════════
  {
    id: 'muscle-up',
    name: 'Muscle-up',
    movementPattern: 'vertical_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['chest', 'triceps', 'biceps', 'abs', 'forearms'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '3-5', defaultRPE: 9, defaultRest: 180, defaultTempo: '2-0-1-0',
  },
  {
    id: 'l-sit',
    name: 'L-sit (podpora)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'quads', 'triceps'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-20s', defaultRPE: 8, defaultRest: 90, defaultTempo: '-',
  },
  {
    id: 'dragon-flag',
    name: 'Dragon flag',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'lats'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '5-8', defaultRPE: 9, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'hindu-push-up',
    name: 'Pompki hinduskie',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps', 'abs', 'hip_flexors'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-15', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-0-2-0',
  },
  {
    id: 'planche-push-up',
    name: 'Pompki w planszy',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts', 'triceps', 'abs', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '3-6', defaultRPE: 9.5, defaultRest: 180, defaultTempo: '3-1-2-0',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - CONDITIONING
  // ═══════════════════════════════════════════
  {
    id: 'battle-ropes',
    name: 'Battle ropes (liny bojowe)',
    movementPattern: 'core',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['abs', 'forearms', 'biceps'],
    tag: 'accessory',
    defaultSets: 4, defaultReps: '30s', defaultRPE: 8, defaultRest: 60, defaultTempo: '-',
  },
  {
    id: 'box-jump',
    name: 'Wyskoki na skrzynkę',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'calves', 'hamstrings'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '5-8', defaultRPE: 7, defaultRest: 120, defaultTempo: '-',
  },
  {
    id: 'sled-push',
    name: 'Pchanie sań',
    movementPattern: 'squat',
    primaryMuscle: 'quads',
    secondaryMuscles: ['glutes', 'calves', 'abs', 'front_delts'],
    tag: 'compound',
    defaultSets: 4, defaultReps: '20m', defaultRPE: 8, defaultRest: 120, defaultTempo: '-',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - SHOULDERS
  // ═══════════════════════════════════════════
  {
    id: 'lu-raise',
    name: 'Lu raise',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'side_delts',
    secondaryMuscles: ['front_delts', 'traps'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'bradford-press',
    name: 'Wyciskanie Bradforda',
    movementPattern: 'vertical_push',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'triceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10', defaultRPE: 7, defaultRest: 120, defaultTempo: '2-0-2-0',
  },
  {
    id: 'bus-driver',
    name: 'Bus driver (obroty talerza)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'front_delts',
    secondaryMuscles: ['side_delts', 'forearms'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - CHEST
  // ═══════════════════════════════════════════
  {
    id: 'svend-press',
    name: 'Svend press (ściskanie talerza)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['front_delts'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '12-15', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-2-2-0',
  },
  {
    id: 'hex-press',
    name: 'Hex press (wyciskanie hantli do siebie)',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'chest',
    secondaryMuscles: ['triceps', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 90, defaultTempo: '2-1-2-0',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - ARMS
  // ═══════════════════════════════════════════
  {
    id: 'bayesian-curl',
    name: 'Uginanie bayesiańskie (wyciąg za plecami)',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'biceps',
    secondaryMuscles: [],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },
  {
    id: 'jm-press',
    name: 'JM press',
    movementPattern: 'horizontal_push',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest', 'front_delts'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '6-8', defaultRPE: 8, defaultRest: 120, defaultTempo: '3-1-2-0',
  },
  {
    id: 'tate-press',
    name: 'Tate press',
    movementPattern: 'isolation_upper',
    primaryMuscle: 'triceps',
    secondaryMuscles: ['chest'],
    tag: 'isolation',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7, defaultRest: 60, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - BACK
  // ═══════════════════════════════════════════
  {
    id: 'meadows-row',
    name: 'Wiosłowanie Meadowsa',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'lats',
    secondaryMuscles: ['upper_back', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '8-10/side', defaultRPE: 8, defaultRest: 90, defaultTempo: '2-1-2-0',
  },
  {
    id: 'seal-row',
    name: 'Wiosłowanie leżąc (seal row)',
    movementPattern: 'horizontal_pull',
    primaryMuscle: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delts', 'biceps'],
    tag: 'compound',
    defaultSets: 3, defaultReps: '10-12', defaultRPE: 7.5, defaultRest: 90, defaultTempo: '2-1-3-0',
  },

  // ═══════════════════════════════════════════
  // ADDITIONAL - CORE
  // ═══════════════════════════════════════════
  {
    id: 'copenhagen-plank',
    name: 'Deska kopenhaska',
    movementPattern: 'core',
    primaryMuscle: 'hip_flexors',
    secondaryMuscles: ['abs', 'glutes'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '15-20s/side', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '-',
  },
  {
    id: 'stir-the-pot',
    name: 'Stir the pot (kręcenie na piłce)',
    movementPattern: 'core',
    primaryMuscle: 'abs',
    secondaryMuscles: ['hip_flexors', 'front_delts'],
    tag: 'accessory',
    defaultSets: 3, defaultReps: '10-12/dir', defaultRPE: 7.5, defaultRest: 60, defaultTempo: '-',
  },
];

/** Quick index for lookups */
export const EXERCISE_MAP = new Map(EXERCISE_DB.map((e) => [e.id, e]));

/** Get all exercises including custom ones */
export function getAllExercises(custom: ExerciseDefinition[] = []): ExerciseDefinition[] {
  return [...EXERCISE_DB, ...custom];
}

/** Build combined map (built-in + custom) */
export function getExerciseMap(custom: ExerciseDefinition[] = []): Map<string, ExerciseDefinition> {
  const map = new Map(EXERCISE_MAP);
  for (const c of custom) map.set(c.id, c);
  return map;
}

/** Get exercises by movement pattern */
export function getExercisesByPattern(pattern: MovementPattern, custom: ExerciseDefinition[] = []): ExerciseDefinition[] {
  return getAllExercises(custom).filter((e) => e.movementPattern === pattern);
}

/** Get exercises by primary muscle */
export function getExercisesByMuscle(muscle: MuscleGroup, custom: ExerciseDefinition[] = []): ExerciseDefinition[] {
  return getAllExercises(custom).filter((e) => e.primaryMuscle === muscle || e.secondaryMuscles.includes(muscle));
}

/** Fuzzy search */
export function searchExercises(query: string, custom: ExerciseDefinition[] = []): ExerciseDefinition[] {
  const all = getAllExercises(custom);
  const normalize = (v: string) =>
    v
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const q = normalize(query);
  if (!q) return all;

  return all.filter(
    (e) => {
      const searchable = [
        e.name,
        e.movementPattern.replace(/_/g, ' '),
        MOVEMENT_PATTERN_LABELS[e.movementPattern],
        e.primaryMuscle.replace(/_/g, ' '),
        MUSCLE_GROUP_LABELS[e.primaryMuscle],
        ...e.secondaryMuscles.map((m) => m.replace(/_/g, ' ')),
        ...e.secondaryMuscles.map((m) => MUSCLE_GROUP_LABELS[m]),
        e.tag,
        EXERCISE_TAG_LABELS[e.tag],
      ].map(normalize);

      return searchable.some((token) => token.includes(q));
    }
  );
}
